import { Badge } from "@/components/ui/badge";
import { SimpleTooltip } from "@/components/ui/simple-tooltip";
import { IPERIA_PE_SIGNATURE_CLOSED_LABEL, iperiaPeSignatureClosed, iperiaPeSignatureClosedMessage } from "./iperia-pe-window";
import { EstimateType } from "@/types/billing/estimate";
import { FundingOrganismType } from "@/types/content/funding_organism";
import { ProductType } from "@/types/content/registration/product";
import { GoCardlessOnboardingType, PreConfiguredRegistrationType } from "@/types/pre_configured_registration/registration";

export const get_product_data = (product: ProductType, registration: PreConfiguredRegistrationType) => {
    // Product catalog
    let pc = null;

    if (registration.product_catalogs && registration.product_catalogs.length > 0) {
        const product_catalogs = registration.product_catalogs.filter(pc => pc.product == product.id);
        if (product_catalogs.length > 0) {
            pc = product_catalogs[0];
        }
    } else if (registration.product_catalog && registration.product_catalog.product == product.id) {
        pc = registration.product_catalog;
    }

    const product_code = pc?.product_code ? pc.product_code : product.product_code;
    const duration = pc?.duration ? pc.duration : product.duration;
    const listing_price = pc?.listing_price ? pc.listing_price : product.listing_price;
    const funding_price = pc?.funding_price ? pc.funding_price : null;
    const minimum_personal_funding = product.minimum_selling_price;

    // Funding organism session
    let funding_organism_session = null;
    const product_registrations = registration.funding_organism_registrations.filter(r => r.product.id == product.id);
    if (product_registrations.length > 0) {
        funding_organism_session = product_registrations[0];
    }
    // Pre registration custom process
    let pre_registration_custom_process_validated = true; /* Init with always validated if not set */
    const pre_registration_custom_processes = registration.pre_registration_custom_processes.filter(prcp => prcp.product == product.id);
    for (var _pre_registration_custom_process of pre_registration_custom_processes) {
        if (!_pre_registration_custom_process.validated) {
            pre_registration_custom_process_validated = false;
        }
    }

    let part_orga = null;
    let compensation = null;
    if (funding_organism_session && !funding_organism_session.cancelled) {
        part_orga = funding_organism_session.part_orga ? parseFloat(funding_organism_session.part_orga) : null;
        compensation = funding_organism_session.compensation ? parseFloat(funding_organism_session.compensation) : null;
    }
    // Return
    return {
        pre_registration_custom_processes: pre_registration_custom_processes,
        pre_registration_custom_process_validated: pre_registration_custom_process_validated,
        product_name: product.name,
        product_code: product_code,
        duration: duration !== null ? duration : 0,
        listing_price: listing_price !== null ? listing_price : 0,
        funding_price: funding_price !== null ? funding_price : 0,
        minimum_personal_funding: minimum_personal_funding !== null ? minimum_personal_funding : 0,
        part_orga: part_orga !== null ? part_orga : 0,
        compensation: compensation !== null ? compensation : 0,
        funding_organism_session: funding_organism_session,
        default_personal_funding: funding_organism_session?.default_personal_funding ?? (listing_price !== null ? listing_price - (funding_price ?? 0) : 0)
    };
};

/**
 * Une formation rattachée à une ProductSession reprend automatiquement les dates de celle-ci
 * pour la convention de formation (cf. OPCOConventionDialog, où ces champs sont désactivés dans ce cas).
 */
export const get_convention_dates = (registration: PreConfiguredRegistrationType) => ({
    starting_date:
        registration.product_session?.convention_starting_date ||
        registration.product_session?.starting_date ||
        registration.convention_starting_date ||
        undefined,
    ending_date:
        registration.product_session?.convention_ending_date ||
        registration.product_session?.ending_date ||
        registration.convention_ending_date ||
        undefined
});

export const registration_status_badge = (
    registration: {
        validated: boolean;
        discarded: boolean;
        pre_registration: boolean;
        funding_organism: FundingOrganismType | null;
        stripe_contract_signed_at: string | null;
        stripe_setup_at: string | null;
        alma_contract_signed_at: string | null;
        alma_payment_id: string | null;
        funding_organism_registration_validated: boolean | null;
        pre_registration_custom_process_validated: boolean | null;
        custom_processes_validation: boolean | null;
        direct_validation: boolean | null;
        dpc_contract_signed_at: string | null;
        personal_funding: number | null;
        default_personal_funding: number | null;
        submitted: boolean | null;
        submitted_at: string | null;
        convention_signed_at?: string | null;
        convention_pdf_url?: string | null;
        estimate?: EstimateType | null;
        funding_reference?: string | null;
        opco_amount?: string | null;
        is_permitted: boolean;
        cpf_reference?: string | null;
        error_message?: string | null;
        positioning_status?: { blocking?: boolean } | null;
        gocardless_onboarding?: GoCardlessOnboardingType | null;
        iperia_bi_flow?: boolean;
        iperia_extension?: {
            workflow_status?: string | null;
            bi_am_signed_at?: string | null;
            bi_pe_signed_at?: string | null;
            pe_signature_last_day?: string | null;
            bi_is_stale?: boolean;
        } | null;
        sf_case?: {
            starting_date: string | null;
            ending_date: string | null;
            organism_part: number | null;
            type: string | null;
            status: string | null;
            dpc_ref_action: string | null;
            dpc_ref_session: string | null;
            cpf_ref_record: string | null;
            walter_session_time: number | null;
            walter_session_progress: number | null;
            realized_rate: number | null;
            contact: {
                last_name: string | null;
                first_name: string | null;
                email: string | null;
                phone: string | null;
                mobile_phone: string | null;
                last_login: string | null;
            } | null;
            created_at: string | null;
        } | null;
    },
    className?: string
) => {
    if (
        registration.iperia_bi_flow &&
        !registration.discarded &&
        !registration.validated &&
        iperiaPeSignatureClosed(registration.iperia_extension)
    ) {
        return (
            <Badge variant="red" className={className}>
                <SimpleTooltip message={iperiaPeSignatureClosedMessage(registration.iperia_extension?.pe_signature_last_day)}>
                    {IPERIA_PE_SIGNATURE_CLOSED_LABEL}
                </SimpleTooltip>
            </Badge>
        );
    }
    if (registration.sf_case && ["Unsubscribed", "NullOrganismPart", "Repositioned", "NotBillable", "MissingBudget", "Interrupted", "Cancelled"].includes(registration.sf_case.status || "")) {
        return (
            <Badge variant="outline" className={className}>
                {registration.sf_case.status}
            </Badge>
        );
    }
    // IPERIA BI: the AM signature is written on the extension, then the case is created
    // on_commit. The list badge must follow the dossier, not wait for that case.
    if (registration.iperia_bi_flow) {
        if (registration.discarded) {
            return (
                <Badge variant="outline" className={className}>
                    Annulée
                </Badge>
            );
        }
        const workflow_status = registration.iperia_extension?.workflow_status;
        if (!registration.validated && workflow_status === "pe_signature_expired") {
            return (
                <Badge variant="red" className={className}>
                    En retard
                </Badge>
            );
        }
        // A stale bulletin still carries the AM signature of the archived document.
        if (!registration.validated && registration.iperia_extension?.bi_is_stale) {
            return (
                <Badge variant="amber" className={className}>
                    <SimpleTooltip message="Le bulletin n'est plus à jour. Ouvrez le dossier, puis Régénérer le bulletin. L'assistante maternelle n'a pas encore reçu le lien du formulaire.">BI à régénérer</SimpleTooltip>
                </Badge>
            );
        }
        if (!registration.validated && (workflow_status === "awaiting_pe_signature" || registration.iperia_extension?.bi_am_signed_at)) {
            return (
                <Badge variant="purple" className={className}>
                    A valider
                </Badge>
            );
        }
        if (!registration.validated && workflow_status === "awaiting_bi_signature") {
            // Périmer le bulletin efface bi_am_signed_at mais laisse vivre le case ouvert par la
            // signature qu'il annule : on redemande alors une signature déjà obtenue une fois.
            if (registration.sf_case) {
                return (
                    <Badge variant="amber" className={className}>
                        <SimpleTooltip message="Le bulletin a été régénéré : l'assistante maternelle reprend le formulaire, puis signe le nouveau bulletin">A re-signer</SimpleTooltip>
                    </Badge>
                );
            }
            return (
                <Badge variant="purple" className={className}>
                    A signer
                </Badge>
            );
        }
    }
    if (registration.sf_case) {
        return (
            <Badge variant="green" className={className}>
                Validée
            </Badge>
        );
    }
    if (registration.discarded) {
        return (
            <Badge variant="outline" className={className}>
                Annulée
            </Badge>
        );
    }
    if (registration.validated) {
        if (registration.pre_registration) {
            return (
                <Badge variant="green" className={className}>
                    Pré-inscrit
                </Badge>
            );
        } else {
            return (
                <Badge variant="green" className={className}>
                    Validée
                </Badge>
            );
        }
    }
    if (registration.error_message) {
        return (
            <Badge variant="red" className={className}>
                Erreur
            </Badge>
        );
    }
    if (!registration.is_permitted) {
        return (
            <Badge variant="red" className={className}>
                <SimpleTooltip message="Une autorisation managériale est nécessaire pour pouvoir poursuivre cette inscription">Attente autorisation</SimpleTooltip>
            </Badge>
        );
    }
    if (!registration.funding_organism) {
        return (
            <Badge variant="outline" className={className}>
                A configurer
            </Badge>
        );
    }

    const documentsPending = registration.custom_processes_validation && !registration.pre_registration_custom_process_validated;
    const positioningBadge =
        !documentsPending && registration.positioning_status?.blocking ? (
            <Badge variant="amber" className={className}>
                <SimpleTooltip message="Le prospect doit compléter le test de positionnement sur Walter Inscription">Test positionnement</SimpleTooltip>
            </Badge>
        ) : null;

    /* Stripe */
    if (registration.funding_organism.code == "STRIPE") {
        if (documentsPending) {
            return (
                <Badge variant="amber" className={className}>
                    <SimpleTooltip message="Des documents d'inscription pédagogiques doivent être complétés">Documents à remplir</SimpleTooltip>
                </Badge>
            );
        }
        if (positioningBadge) return positioningBadge;
        if (!registration.stripe_contract_signed_at) {
            return (
                <Badge variant="gray" className={className}>
                    Signature contrat
                </Badge>
            );
        } else if (registration.stripe_setup_at) {
            return (
                <Badge variant="amber" className={className}>
                    Paiement Stripe
                </Badge>
            );
        } else {
            return (
                <Badge variant="gray" className={className}>
                    Saisie info CB
                </Badge>
            );
        }
    }
    /* Alma */
    if (registration.funding_organism.code == "ALMA") {
        if (documentsPending) {
            return (
                <Badge variant="amber" className={className}>
                    <SimpleTooltip message="Des documents d'inscription pédagogiques doivent être complétés">Documents à remplir</SimpleTooltip>
                </Badge>
            );
        }
        if (positioningBadge) return positioningBadge;
        if (!registration.alma_contract_signed_at) {
            return (
                <Badge variant="gray" className={className}>
                    Signature contrat
                </Badge>
            );
        } else if (registration.alma_payment_id) {
            return (
                <Badge variant="amber" className={className}>
                    Paiement Alma
                </Badge>
            );
        } else {
            return (
                <Badge variant="gray" className={className}>
                    A payer
                </Badge>
            );
        }
    }
    /* GoCardless */
    if (registration.funding_organism.code == "GOCARDLESS") {
        if (documentsPending) {
            return (
                <Badge variant="amber" className={className}>
                    <SimpleTooltip message="Des documents d'inscription pédagogiques doivent être complétés">Documents à remplir</SimpleTooltip>
                </Badge>
            );
        }
        if (positioningBadge) return positioningBadge;
        const onboarding = registration.gocardless_onboarding;
        const setupStatus = onboarding?.setup_status;
        if (setupStatus === "awaiting_signature" || onboarding?.contract_signed === false) {
            return (
                <Badge variant="gray" className={className}>
                    Signature contrat
                </Badge>
            );
        }
        if (setupStatus === "awaiting_mandate") {
            return (
                <Badge variant="gray" className={className}>
                    Mandat SEPA
                </Badge>
            );
        }
        if (setupStatus === "awaiting_first_payment") {
            return (
                <Badge variant="gray" className={className}>
                    Saisie carte
                </Badge>
            );
        }
        if (onboarding?.session_created || setupStatus === "active") {
            return (
                <Badge variant="amber" className={className}>
                    Attente paiement
                </Badge>
            );
        }
        return (
            <Badge variant="amber" className={className}>
                GoCardless
            </Badge>
        );
    }
    /* Virement */
    if (registration.funding_organism.code == "BANK_TRANSFER") {
        if (documentsPending) {
            return (
                <Badge variant="amber" className={className}>
                    <SimpleTooltip message="Des documents d'inscription pédagogiques doivent être complétés">Documents à remplir</SimpleTooltip>
                </Badge>
            );
        }
        if (positioningBadge) return positioningBadge;
        if (!registration.alma_contract_signed_at) {
            return (
                <Badge variant="gray" className={className}>
                    Signature contrat
                </Badge>
            );
        } else {
            return (
                <Badge variant="amber" className={className}>
                    Paiement Virement
                </Badge>
            );
        }
    }
    /* DPC */
    if (registration.funding_organism.code == "DPC") {
        if (!registration.funding_organism_registration_validated && !registration.pre_registration) {
            return (
                <Badge variant="gray" className={className}>
                    Inscription DPC
                </Badge>
            );
        } else if (documentsPending) {
            return (
                <Badge variant="amber" className={className}>
                    Documents à signer
                </Badge>
            );
        }
        if (positioningBadge) return positioningBadge;
        if (registration.direct_validation) {
            return (
                <Badge variant="purple" className={className}>
                    A finaliser
                </Badge>
            );
        } else if (!registration.dpc_contract_signed_at) {
            return (
                <Badge variant="gray" className={className}>
                    Signature contrat
                </Badge>
            );
        } else if ((registration.personal_funding !== null && registration.personal_funding) || (registration.personal_funding === null && registration.default_personal_funding)) {
            return (
                <Badge variant="amber" className={className}>
                    Reste à charge
                </Badge>
            );
        } else if (registration.submitted) {
            return (
                <Badge variant="purple" className={className}>
                    A valider
                </Badge>
            );
        }
    }

    /* FIFPL */
    if (["FIFPL"].includes(registration.funding_organism.code)) {
        if (documentsPending) {
            return (
                <Badge variant="amber" className={className}>
                    <SimpleTooltip message="Des documents d'inscription pédagogiques doivent être complétés">Documents à remplir</SimpleTooltip>
                </Badge>
            );
        }
        if (positioningBadge) return positioningBadge;
        if ((registration.personal_funding !== null && registration.personal_funding) || (registration.personal_funding === null && registration.default_personal_funding)) {
            return (
                <Badge variant="amber" className={className}>
                    Reste à charge
                </Badge>
            );
        } else if (registration.submitted) {
            if (registration.direct_validation) {
                return (
                    <Badge variant="purple" className={className}>
                        Validation...
                    </Badge>
                );
            } else {
                return (
                    <Badge variant="purple" className={className}>
                        A valider
                    </Badge>
                );
            }
        }
    }

    /* IPERIA */
    if (["IPERIA"].includes(registration.funding_organism.code)) {
        if (documentsPending) {
            return (
                <Badge variant="amber" className={className}>
                    <SimpleTooltip message="Des documents d'inscription pédagogiques doivent être complétés">Documents à remplir</SimpleTooltip>
                </Badge>
            );
        }
        if (positioningBadge) return positioningBadge;
        if ((registration.personal_funding !== null && registration.personal_funding) || (registration.personal_funding === null && registration.default_personal_funding)) {
            return (
                <Badge variant="amber" className={className}>
                    Reste à charge
                </Badge>
            );
        } else if (registration.submitted) {
            return (
                <Badge variant="purple" className={className}>
                    <SimpleTooltip message="Le TypeForm 1 a été rempli par le prospect">TF1 rempli</SimpleTooltip>
                </Badge>
            );
        }
    }

    /* CPF */
    if (registration.funding_organism.code == "CPF") {
        if (documentsPending) {
            return (
                <Badge variant="amber" className={className}>
                    <SimpleTooltip message="Des documents d'inscription pédagogiques doivent être complétés">Documents à remplir</SimpleTooltip>
                </Badge>
            );
        }
        if (positioningBadge) return positioningBadge;
        if (!registration.cpf_reference) {
            return (
                <Badge variant="gray" className={className}>
                    <SimpleTooltip message="La demande d'inscription CPF n'a pas encore été reçue (étape 1/3)">Inscription CPF</SimpleTooltip>
                </Badge>
            );
        }
        if (!registration.validated) {
            return (
                <Badge variant="blue" className={className}>
                    <SimpleTooltip message="Le prospect doit valider son inscription sur son compte CPF (étape 2/3)">Validation CPF</SimpleTooltip>
                </Badge>
            );
        }
    }

    /* OPCO */
    if (registration.funding_organism.type == "opco") {
        if (documentsPending) {
            return (
                <Badge variant="amber" className={className}>
                    <SimpleTooltip message="Des documents d'inscription pédagogiques doivent être complétés">Documents à remplir</SimpleTooltip>
                </Badge>
            );
        }
        if (positioningBadge) return positioningBadge;
        if (!registration.convention_pdf_url || !registration.estimate) {
            return (
                <Badge variant="outline" className={className}>
                    <SimpleTooltip message="Vous devez compléter le devis et la convention puis l'envoyer au prospect">Devis à envoyer</SimpleTooltip>
                </Badge>
            );
        }
        if (!registration.convention_signed_at) {
            return (
                <Badge variant="amber" className={className}>
                    <SimpleTooltip message="Le prospect doit signer la convention de formation">Signature convention</SimpleTooltip>
                </Badge>
            );
        }
        if (!registration.funding_reference || registration.funding_reference === "TIERS_DECLARANT") {
            return (
                <Badge variant="gray" className={className}>
                    <SimpleTooltip message="Le prospect doit déposer sa demande de prise en charge auprès de l'OPCO">Dépôt OPCO</SimpleTooltip>
                </Badge>
            );
        }
        if (registration.opco_amount === null) {
            return (
                <Badge variant="blue" className={className}>
                    <SimpleTooltip message="En attente de la notification de prise en charge par l'OPCO">En attente OPCO</SimpleTooltip>
                </Badge>
            );
        }

        if (registration.personal_funding !== null) {
            return (
                <Badge variant="amber" className={className}>
                    <SimpleTooltip message="Le prospect doit régler le reste à charge">Reste à charge</SimpleTooltip>
                </Badge>
            );
        }

        return (
            <Badge variant="purple" className={className}>
                A valider
            </Badge>
        );
    }

    return (
        <Badge variant="default" className={className}>
            En attente
        </Badge>
    );
};

export const get_product_color = (registration: PreConfiguredRegistrationType, product: ProductType, _default = "text-gray-500") => {
    const registrations = registration.funding_organism_registrations.filter(_for => _for.product.id == product.id);
    if (registrations.length > 0) {
        return !registrations[0].cancelled ? "text-green-600" : "text-amber-600";
    }
    return _default;
};

export const registration_products_list = (registration: PreConfiguredRegistrationType, withStatus: boolean = true) => {
    if (registration.products.length > 0) {
        return registration.products.map((p, i) => {
            return (
                <span key={p.product_code} className={withStatus ? get_product_color(registration, p) : ""}>
                    {i > 0 && ", "}
                    {p.product_code}
                </span>
            );
        });
    } else if (registration.product) {
        return <span className={get_product_color(registration, registration.product)}>{registration.product.product_code}</span>;
    } else {
        return <span className="italic text-red-600">Aucun produit</span>;
    }
};

export const registration_part_orga_label = (registration: PreConfiguredRegistrationType) => {
    const formatter = new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" });
    if (registration.funding_organism?.code == "GIVEAWAY") {
        return formatter.format(0);
    }
    if (registration.funding_organism?.code == "CPF") {
        if (registration.cpf_price) {
            return formatter.format(parseFloat(registration.cpf_price || "0"));
        }
    }
    if (registration.funding_organism?.code == "DPC") {
        if (registration.funding_organism_registration_validated) {
            let part_orga = registration.funding_organism_registrations.reduce((acc, _for) => acc + parseFloat(_for.part_orga || "0"), 0);
            let personal_funding = registration.personal_funding ?? registration.default_personal_funding ?? 0;
            if (personal_funding) {
                return `${formatter.format(part_orga)} + ${formatter.format(personal_funding)}`;
            }
            return formatter.format(part_orga);
        }
    }
    if (["FIFPL", "IPERIA", "FAFPM"].includes(registration.funding_organism?.code || "") || registration.funding_organism?.type == "opco") {
        let part_orga = (registration.products || [registration.product]).reduce((acc, p) => {
            const pc = (registration.product_catalogs || [registration.product_catalog]).find(_pc => _pc && _pc.product == p.id && _pc.funding_organism == registration.funding_organism?.code);
            if (pc && pc.funding_price) {
                return acc + pc.funding_price;
            } else if (p.listing_price) {
                return acc + p.listing_price;
            } else {
                return acc + (p.listing_price || 0);
            }
        }, 0);

        let personal_funding = registration.personal_funding ?? registration.default_personal_funding ?? 0;
        if (personal_funding) {
            return `${formatter.format(part_orga)} + ${formatter.format(personal_funding)}`;
        }
        return formatter.format(part_orga);
    }
    if (["ALMA", "GOCARDLESS", "BANK_TRANSFER"].includes(registration.funding_organism?.code || "")) {
        if (registration.personal_funding || registration.default_personal_funding) {
            return formatter.format(registration.personal_funding ?? registration.default_personal_funding ?? 0);
        }
    }

    return null;
};

/** Affiche le bloc positionnement sur l’onglet Formation (test activé sur au moins un produit). */
export const isPositioningSectionVisible = (registration: PreConfiguredRegistrationType): boolean => {
    const hasPositioning =
        (registration.positioning_status?.products?.length ?? 0) > 0 ||
        (registration.positioning_outcomes?.length ?? 0) > 0;
    const customProcessesBlock =
        registration.custom_processes_validation && !registration.pre_registration_custom_process_validated;
    return hasPositioning && !customProcessesBlock;
};
